// Properties: the calculators of the server (GET /properties/registry), the check of a model and its calculations
// (POST /properties/check), the properties.<family> jobs, the generated form's values, and the figures of the typed
// result views (curve, bands, map, table) of a prayog-property-result.
import { get, post } from "./api";
import { defaultMemory, type NegfFit, type NegfMemory } from "./negf";

export type InputKind = "number" | "choice" | "bool" | "mesh" | "energy_grid" | "number_list" | "vector" | "bands" | "kpath" | "files";
export interface InputSchema {
  kind: InputKind; label: string; default: any; help?: string; advanced?: boolean;
  unit?: string; min?: number | null; max?: number | null; integer?: boolean;
  options?: { value: string; label: string }[];
}
export interface CalcDesc {
  id: string; family: string; title: string; version: number; help: string; formula: string;
  requires: Record<string, unknown>; engines: string[]; inputs: Record<string, InputSchema>;
}
export interface Family { id: string; title: string; installed: boolean; allowed?: boolean }
export interface EngineStatus { ok: boolean; title: string; version?: string | null; message?: string }
export interface PropRegistry {
  families: Family[]; calculators: CalcDesc[]; limits: Record<string, number | boolean | null>;
  engines?: Record<string, EngineStatus>;                // which engines can be used on this server
}
export interface Issue { level: "error" | "warning"; message: string; fix?: string }
export interface CalcCheck {
  calculator: string; issues: Issue[]; refused: string | null; limit?: string;
  cost: { kpoints: number; matrix: number; seconds: number } | null; inputs?: Record<string, unknown>; engine?: string;
}
export interface PropCheck { model: Record<string, any>; name: string; eligible: Record<string, Issue[]>; calculations: CalcCheck[] }

export type View =
  | { type: "curve"; title: string; x: { label: string; values: number[] }; series: { name: string; values: (number | null)[]; error?: (number | null)[]; axis?: number }[];
      y_label: string; y2_label?: string; ticks?: [number, string][]; bands_from?: number; marker_x?: number }
  | { type: "bands"; title: string; x: number[]; energies: number[][]; ticks: [number, string][]; y_label: string;
      band_index?: number[]; window?: number[]; weights?: Record<string, number[][]>; spin_z?: number[][]; overlay?: boolean }
  | { type: "map"; title: string; kx: number[]; ky: number[]; values: number[]; label: string; outline?: number[][]; symmetric?: boolean }
  | { type: "table"; title: string; headers: string[]; rows: (string | number)[][] };
export interface PropertyResult {
  format: string; calculator: { id: string; title: string; version: number; family: string }; engine: string;
  inputs: Record<string, unknown>; model: Record<string, unknown>; conventions: Record<string, string>;
  seconds: number; warnings: string[]; notes: string[]; summary: [string, string][]; views: View[]; data: Record<string, unknown>;
  files?: Record<string, string>; versions?: Record<string, string>;
}

/** What a Properties project remembers: the model (the same fields as NEGF's model step) and the property tool. */
export interface PropertiesMemory extends NegfMemory {
  family?: string;
  calculator?: string;
  inputs?: Record<string, Record<string, unknown>>;      // per calculator: the form's values
  engines?: Record<string, string>;                      // per calculator: the engine chosen (default native)
  prop_jobs?: Record<string, string>;                    // per calculator: the run shown
  preset?: PropertiesPreset;                             // continued from an illustrative project (server-set)
  from_example?: { id: string; try: string; title: string; about: string };   // made by an illustrative project's "try this"
}
/** The calculations an illustrative project continues with in Properties (example.json properties_continue). */
export interface PropertiesPreset {
  kind?: "continue" | "example";       // continuing a Materials project (default), or a Properties illustrative project
  example: string; name: string; what: string;
  calculations: { calculator: string; inputs: Record<string, unknown>; note?: string }[];
  counterpart?: string | null;         // "continue": the Properties illustrative project of the same fit
  modifiers?: Record<string, unknown>[];   // "continue": model changes to apply first (Rashba, exchange, ...)
}
export function defaultPropertiesMemory(): PropertiesMemory {
  return { ...defaultMemory(), family: "electronic", calculator: "electronic.bands", inputs: {}, prop_jobs: {} };
}

// -- API ---------------------------------------------------------------------------------------------------------
export const propertiesRegistry = (projectId?: string) =>
  get<PropRegistry>(`/properties/registry${projectId ? `?project_id=${projectId}` : ""}`);
export interface CalcSpec { calculator: string; inputs: Record<string, unknown>; engine?: string }
export const propertiesCheck = (model_file: string, calculations: CalcSpec[], project_id?: string) =>
  post<PropCheck>("/properties/check", { model_file, calculations, project_id });
export const propertiesFits = () => get<NegfFit[]>("/properties/fits");

/** "Open in Properties" / "Continue in Properties" (SKFitter): a new Properties project whose model is being built from
 * this fit (POST /properties/continue queues the properties.model job). A fit of an illustrative project continues the
 * example: its calculations are suggested (memory.preset) and the project keeps the example's resources. */
export const continueInProperties = (fitJobId: string) => post<{ id: string }>("/properties/continue", { fit_job: fitJobId });

// -- the generated form -------------------------------------------------------------------------------------------
export function defaultInputs(c: CalcDesc): Record<string, unknown> {
  return Object.fromEntries(Object.entries(c.inputs).map(([k, s]) => [k, structuredClone(s.default)]));
}

/** The calculator's inputs that differ from their defaults (what the job sends; the server fills the rest). */
export function changedInputs(c: CalcDesc, values: Record<string, unknown> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, s] of Object.entries(c.inputs)) {
    const v = values?.[k];
    if (v !== undefined && JSON.stringify(v) !== JSON.stringify(s.default)) out[k] = v;
  }
  return out;
}

const frac = (t: string) => {
  const m = /^(-?\d+(?:\.\d*)?)\s*\/\s*(\d+(?:\.\d*)?)$/.exec(t.trim());
  return m ? Number(m[1]) / Number(m[2]) : Number(t);
};
/** "Γ 0 0 0; M 1/2 0 0; K 1/3 1/3 0 | A 0 0 1/2; ..." -> kpath input ("|" is a jump); "" -> "auto". */
export function parsePath(text: string): "auto" | { points: ([string, number[]] | null)[] } {
  if (!text.trim()) return "auto";
  const points: ([string, number[]] | null)[] = [];
  text.split("|").forEach((piece, n) => {
    if (n > 0) points.push(null);
    for (const part of piece.split(/[;\n]+/).map((p) => p.trim()).filter(Boolean)) {
      const [label, ...ks] = part.split(/[\s,]+/).filter(Boolean);
      const k = ks.map(frac);
      if (!label || k.length !== 3 || k.some((x) => !Number.isFinite(x))) throw new Error(`Path point "${part}": write a label and three fractional k.`);
      points.push([label, k]);
    }
  });
  if (points.filter(Boolean).length < 2) throw new Error("A path needs at least two points.");
  return { points };
}
export function pathText(v: unknown): string {
  if (!v || v === "auto") return "";
  const pts = ((v as { points?: unknown[] }).points ?? []) as ([string, number[]] | null)[];
  return pts.map((p) => (p === null ? "|" : `${p[0]} ${p[1].map((x) => +x.toFixed(6)).join(" ")}`)).join("; ").replace(/; \|; /g, " | ");
}

/** Problems of the form found before asking the server. */
export function formProblems(c: CalcDesc, values: Record<string, unknown>): string[] {
  const out: string[] = [];
  for (const [k, s] of Object.entries(c.inputs)) {
    const v = values[k];
    if (s.kind === "number" && typeof v === "number") {
      if (s.min != null && v < s.min) out.push(`${s.label}: at least ${s.min}.`);
      if (s.max != null && v > s.max) out.push(`${s.label}: at most ${s.max}.`);
    }
    if (s.kind === "energy_grid") {
      const e = v as { min: number; max: number; n: number };
      if (e.n > 1 && !(e.max > e.min)) out.push(`${s.label}: max > min.`);
    }
    if (s.kind === "mesh" && (v as number[]).some((x) => !(x >= 1))) out.push(`${s.label}: at least 1 point per direction.`);
  }
  return out;
}

export function describeProperties(params: Record<string, any>): string {
  const calcs = (params.calculations ?? []) as { calculator: string; inputs?: Record<string, any>; engine?: string }[];
  return calcs.map((c) => {
    const i = c.inputs ?? {};
    const mesh = Array.isArray(i.mesh) ? ` ${i.mesh.join("×")}` : i.nk ? ` ${i.nk}×${i.nk}` : "";
    const engine = c.engine && c.engine !== "native" ? ` (${engineTitle(c.engine)})` : "";
    return `${c.calculator.split(".")[1].replace(/_/g, " ")}${mesh}${engine}`;
  }).join(", ");
}

// -- engines ------------------------------------------------------------------------------------------------------
const ENGINE_TITLES: Record<string, string> = { native: "Native", wannierberri: "WannierBerri" };
export const engineTitle = (id: string, reg?: PropRegistry) => ENGINE_TITLES[id] ?? reg?.engines?.[id]?.title ?? id;

/** The engines of a calculator for the selector: unavailable ones disabled with the server's reason. */
export function engineChoices(calc: CalcDesc, reg?: PropRegistry): { value: string; label: string; disabled: boolean; reason: string }[] {
  return calc.engines.map((e) => {
    const st = e === "native" ? { ok: true } as EngineStatus : reg?.engines?.[e];
    const ok = !!st?.ok;
    const label = engineTitle(e, reg) + (ok && st?.version ? ` ${st.version}` : "");
    return { value: e, label, disabled: !ok, reason: ok ? "" : st?.message || `${engineTitle(e, reg)} is not available on this server.` };
  });
}

/** The engine to send: the one chosen if it can be used, else native. */
export function chosenEngine(calc: CalcDesc, reg: PropRegistry | undefined, wanted?: string): string {
  const c = engineChoices(calc, reg).find((x) => x.value === wanted);
  return c && !c.disabled ? c.value : "native";
}

// -- figures ------------------------------------------------------------------------------------------------------
const COLORS = ["#2e213e", "#eb6834", "#2a78d6", "#1baf7a", "#e87ba4", "#eda100", "#4a3aa7", "#e34948", "#008300", "#7d7d85"];

function tickAxis(ticks?: [number, string][]) {
  if (!ticks?.length) return {};
  return { tickvals: ticks.map((t) => t[0]), ticktext: ticks.map((t) => t[1]), showgrid: false, zeroline: false,
    range: [ticks[0][0], ticks[ticks.length - 1][0]] };
}
function tickLines(ticks?: [number, string][]) {
  return (ticks ?? []).map(([x]) => ({ type: "line", x0: x, x1: x, yref: "paper", y0: 0, y1: 1, line: { color: "#ccc", width: 1 } }));
}

/** A curve view: the totals solid; per-band series (from ``bands_from``) thin, hidden when there are many. */
export function curveFigure(v: Extract<View, { type: "curve" }>) {
  const from = v.bands_from ?? v.series.length;
  const many = v.series.length - from > 8;
  const data = v.series.map((s, i) => ({
    type: "scatter", mode: v.x.values.length < 40 ? "lines+markers" : "lines", name: s.name, x: v.x.values, y: s.values,
    line: { color: COLORS[i % COLORS.length], width: i < from ? 2 : 1, dash: i >= from ? "dot" : "solid" },
    visible: i >= from && many ? "legendonly" : true,
    error_y: s.error ? { type: "data", array: s.error, visible: true } : undefined,
    yaxis: s.axis === 2 ? "y2" : undefined,
  }));
  const shapes: unknown[] = tickLines(v.ticks);
  if (v.marker_x != null) shapes.push({ type: "line", x0: v.marker_x, x1: v.marker_x, yref: "paper", y0: 0, y1: 1, line: { color: "#999", dash: "dash", width: 1 } });
  return { data, layout: { title: { text: v.title }, shapes, xaxis: { title: { text: v.ticks?.length ? "" : v.x.label }, ...tickAxis(v.ticks) },
    yaxis: { title: { text: v.y_label } }, legend: { orientation: "h", y: -0.2 },
    ...(v.y2_label ? { yaxis2: { title: { text: v.y2_label }, overlaying: "y", side: "right", showgrid: false },
      margin: { l: 60, r: 70, t: 40, b: 50 } } : {}) } };
}

/** Bands as one trace (null between bands), optionally with "fat bands" of one weight group or ⟨σz⟩ colours. */
export function bandsFigure(views: Extract<View, { type: "bands" }>[], colour?: { group?: string; spin?: boolean }) {
  const data: unknown[] = [];
  views.forEach((v, n) => {
    const xs: (number | null)[] = [], ys: (number | null)[] = [];
    const nb = v.energies[0]?.length ?? 0;
    for (let b = 0; b < nb; b++) {
      for (let i = 0; i < v.x.length; i++) { xs.push(v.x[i]); ys.push(v.energies[i][b]); }
      xs.push(null); ys.push(null);
    }
    const plain = !colour?.group && !colour?.spin;
    data.push({ type: "scatter", mode: "lines", name: views.length > 1 ? v.title : "bands", x: xs, y: ys, hoverinfo: "y",
      line: { color: views.length > 1 ? COLORS[n % COLORS.length] : plain ? "#2e213e" : "#bbb", width: plain || views.length > 1 ? 1.3 : 0.8 } });
    if (n === 0 && colour?.group && v.weights?.[colour.group]) {
      const w = v.weights[colour.group];
      const px: number[] = [], py: number[] = [], ps: number[] = [];
      for (let i = 0; i < v.x.length; i++) for (let b = 0; b < nb; b++) {
        if (w[i][b] > 0.02) { px.push(v.x[i]); py.push(v.energies[i][b]); ps.push(2 + 10 * w[i][b]); }
      }
      data.push({ type: "scatter", mode: "markers", name: `weight: ${colour.group}`, x: px, y: py,
        marker: { size: ps, color: "#eb6834", opacity: 0.6, line: { width: 0 } }, hoverinfo: "skip" });
    }
    if (n === 0 && colour?.spin && v.spin_z) {
      const px: number[] = [], py: number[] = [], pc: number[] = [];
      for (let i = 0; i < v.x.length; i++) for (let b = 0; b < nb; b++) { px.push(v.x[i]); py.push(v.energies[i][b]); pc.push(v.spin_z[i][b]); }
      data.push({ type: "scatter", mode: "markers", name: "⟨σz⟩", x: px, y: py, hoverinfo: "skip",
        marker: { size: 4, color: pc, colorscale: "RdBu", reversescale: true, cmin: -1, cmax: 1, colorbar: { title: { text: "⟨σz⟩" } } } });
    }
  });
  const v0 = views[0];
  return { data, layout: { title: { text: views.length > 1 ? "Model comparison" : v0.title }, shapes: tickLines(v0.ticks),
    xaxis: tickAxis(v0.ticks), yaxis: { title: { text: v0.y_label }, range: v0.window },
    legend: { orientation: "h", y: -0.12 }, showlegend: views.length > 1 || !!colour?.group } };
}

/** A k-plane map: markers coloured by value (diverging and symmetric for a signed quantity), with the zone outline. */
export function mapFigure(v: Extract<View, { type: "map" }>) {
  const finite = v.values.filter((x) => Number.isFinite(x));
  const sorted = finite.map(Math.abs).sort((a, b) => a - b);
  const vmax = Math.max(1e-12, sorted[Math.floor(0.99 * (sorted.length - 1))] ?? 1);  // robust against spikes
  const n = Math.sqrt(v.values.length);
  const data: unknown[] = [{ type: "scatter", mode: "markers", x: v.kx, y: v.ky, text: v.values.map((x) => x.toPrecision(4)),
    hovertemplate: "k (%{x:.3f}, %{y:.3f}) 1/Å<br>%{text}<extra></extra>",
    marker: { symbol: "square", size: Math.max(3, Math.min(14, 420 / n)), color: v.values,
      colorscale: v.symmetric ? "RdBu" : "Viridis", reversescale: !!v.symmetric,
      ...(v.symmetric ? { cmin: -vmax, cmax: vmax } : {}), colorbar: { title: { text: v.label } } } }];
  if (v.outline) data.push({ type: "scatter", mode: "lines", x: v.outline.map((p) => p[0]), y: v.outline.map((p) => p[1]),
    line: { color: "#555", width: 1 }, hoverinfo: "skip", showlegend: false });
  return { data, layout: { title: { text: v.title }, xaxis: { title: { text: "kx (1/Å)" } },
    yaxis: { title: { text: "ky (1/Å)" }, scaleanchor: "x", scaleratio: 1 }, showlegend: false } };
}

export const fmtSeconds = (s: number) => (s < 1 ? "under a second" : s < 90 ? `about ${Math.round(s)} s` : `about ${Math.round(s / 60)} min`);

// -- formulas: the calculators' LaTeX as readable Unicode (a small subset: Greek, ∂, Σ, Π, fractions, scripts) ------
const GREEK: Record<string, string> = { alpha: "α", beta: "β", gamma: "γ", delta: "δ", sigma: "σ", Omega: "Ω", Gamma: "Γ",
  pi: "π", mu: "μ", hbar: "ħ", partial: "∂", sum: "Σ", prod: "Π", neq: "≠", langle: "⟨", rangle: "⟩", square: "□",
  cdot: "·", to: "→", infty: "∞", ln: "ln", arg: "arg", nabla: "∇", min: "min", max: "max" };
const SUP: Record<string, string> = { "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴", "5": "⁵", "6": "⁶", "7": "⁷", "8": "⁸", "9": "⁹",
  "+": "⁺", "-": "⁻", n: "ⁿ", i: "ⁱ", s: "ˢ", "γ": "ᵞ", "β": "ᵝ", "α": "ᵅ" };
const SUB: Record<string, string> = { "0": "₀", "1": "₁", "2": "₂", "3": "₃", "4": "₄", "5": "₅", "6": "₆", "7": "₇", "8": "₈", "9": "₉",
  "+": "₊", "-": "₋", a: "ₐ", e: "ₑ", i: "ᵢ", j: "ⱼ", k: "ₖ", m: "ₘ", n: "ₙ", r: "ᵣ", s: "ₛ", x: "ₓ", "β": "ᵦ", "γ": "ᵧ",
  "□": "□" };

function group(s: string, i: number): [string, number] {
  if (s[i] === "{") {
    let depth = 0;
    for (let j = i; j < s.length; j++) {
      if (s[j] === "{") depth++;
      if (s[j] === "}" && --depth === 0) return [s.slice(i + 1, j), j + 1];
    }
    return [s.slice(i + 1), s.length];
  }
  if (s[i] === "\\") {
    const m = /^\\([A-Za-z]+|.)/.exec(s.slice(i));
    if (m) return [m[0], i + m[0].length];
  }
  return [s[i] ?? "", i + 1];
}
const script = (t: string, table: Record<string, string>, mark: string) =>
  [...t].every((ch) => table[ch]) ? [...t].map((ch) => table[ch]).join("") : `${mark}${t.length > 1 ? `(${t})` : t}`;

/** "\frac{e^2}{\hbar}\sum_{\mathbf k} f_n\,\Omega_n" -> "(e²)/(ħ) Σₖ fₙ Ωₙ". */
export function prettyFormula(tex: string): string {
  let out = "";
  let i = 0;
  while (i < tex.length) {
    const ch = tex[i];
    if (ch === "\\") {
      const m = /^\\([A-Za-z]+|.)/.exec(tex.slice(i));
      if (!m) { i++; continue; }
      const cmd = m[1];
      i += m[0].length;
      if (cmd === "frac") {
        const [a, j] = group(tex, i);
        const [b, k] = group(tex, j);
        const wrap = (t: string) => (/[\s+\-−]/.test(t.trim()) ? `(${t.trim()})` : t.trim());
        out += ` ${wrap(prettyFormula(a))}/${wrap(prettyFormula(b))} `;
        i = k;
      } else if (cmd === "sqrt") {
        const [a, j] = group(tex, i);
        out += `√(${prettyFormula(a)})`;
        i = j;
      } else if (cmd === "mathrm" || cmd === "mathbf" || cmd === "text") {
        while (tex[i] === " ") i++;
        const [a, j] = group(tex, i);
        out += prettyFormula(a);
        i = j;
      } else if (cmd === "," || cmd === ";") out += " ";
      else if (cmd === "quad") out += "   ";
      else if (cmd === "left" || cmd === "right") { /* drop */ }
      else out += GREEK[cmd] ?? cmd;
    } else if (ch === "^" || ch === "_") {
      const [a, j] = group(tex, i + 1);
      out += script(prettyFormula(a), ch === "^" ? SUP : SUB, ch);
      i = j;
    } else if (ch === "{" || ch === "}") i++;
    else { out += ch; i++; }
  }
  return out.replace(/ {2}(?! )/g, " ").replace(/ {4,}/g, "   ").replace(/\( /g, "(").replace(/ \)/g, ")").trim();
}
